<script setup>
import { computed, ref, watch } from 'vue';
import ScoringSystemInput from '@/components/fields/ScoringSystemInput.vue';
import LiveCounter from '@/components/scoring/LiveCounter.vue';
import TlDialog from '@/components/TlDialog.vue';
import { useScoringPreview } from '@/composables/useScoringPreview';

// "Preview score input" for the admin scoring-system selector: opens a dialog with two
// dummy teams so an admin can try the exact entry players will get — the classic result
// entry, and (when the schema supports it) the live counter — before committing the
// system to a competition. Everything is local; nothing is saved anywhere.
const props = defineProps({
  scoringSystemId: { type: String, default: null },
  // per-competition point overrides ({scoringSystemOutcomeId, homePoints, awayPoints, ...})
  // — the preview must show the points THIS competition actually awards
  overrides: { type: Array, default: () => [] },
  // X200: a scoring system that does not exist yet (the setup assistant's drafted step): an async
  // function returning the system as the scoring system detail (schema, outcomes); used instead of the id.
  load: { type: Function, default: null },
  label: { type: String, default: 'Preview score input' },
});

const dialog = ref(false);
const tab = ref('entry');

const homeTeam = { id: 'preview-home', name: 'Home', players: [] };
const awayTeam = { id: 'preview-away', name: 'Away', players: [] };

const { item: scoringSystem, getItem, itemPending: crudPending } = useCrudApi('api/v2/scoringSystems');
const loading = ref(false);
const loadError = ref('');
const itemPending = computed(() => crudPending.value || loading.value);

// result-entry preview state (mirrors useFixtureForm's game handling)
const games = ref([]);
const homeResult = ref(null);
const awayResult = ref(null);
const previewSystem = ref(null); // local copy so toggling split-games never touches real data

const preview = useScoringPreview();
const supportsLive = computed(() => preview.schema.value?.entryModes?.includes('live') ?? false);

const resetEntry = () => {
  if (!previewSystem.value) return;
  homeResult.value = null;
  awayResult.value = null;
  games.value = Array.from({ length: previewSystem.value.numberGames ?? 0 },
      (_, i) => ({ homeTeamScore: null, awayTeamScore: null, sort: i + 1 }));
};

// same outcome matching players get when recording a real result
const calculateScore = () => {
  if (!previewSystem.value?.splitGames || games.value.length === 0) return;
  let homeGames = 0;
  let awayGames = 0;
  games.value.forEach((game) => {
    const home = parseInt(game.homeTeamScore);
    const away = parseInt(game.awayTeamScore);
    if (home > away) homeGames++;
    else if (away > home) awayGames++;
  });
  const outcome = previewSystem.value.outcomes.find(
      (o) => o.homeGames === homeGames && o.awayGames === awayGames);
  homeResult.value = outcome?.homeResult || '-';
  awayResult.value = outcome?.awayResult || '-';
};
watch(games, calculateScore, { deep: true });

const open = async () => {
  dialog.value = true;
  tab.value = 'entry';
  loadError.value = '';
  let system;
  if (props.load) {
    loading.value = true;
    previewSystem.value = null;
    try {
      system = await props.load();
    } catch (e) {
      loadError.value = e?.global?.[0] ?? e?.data?.global?.[0] ?? e?.message ?? "The preview couldn't be loaded.";
    } finally {
      loading.value = false;
    }
    if (!system) {
      if (!loadError.value) loadError.value = "The preview couldn't be loaded.";
      return;
    }
  } else {
    await getItem(props.scoringSystemId);
    system = scoringSystem.value;
  }
  previewSystem.value = JSON.parse(JSON.stringify(system));
  resetEntry();
  if (previewSystem.value.schema) {
    try {
      preview.load(previewSystem.value.schema, previewSystem.value.schemaParamValues);
    } catch {
      // unparsable schema: entry preview still works, live tab just hides
    }
  }
};

const matchedOutcome = computed(() => {
  if (!previewSystem.value || homeResult.value == null || homeResult.value === '-') return null;
  return previewSystem.value.outcomes?.find(
      (o) => o.homeResult === homeResult.value && o.awayResult === awayResult.value) ?? null;
});

// The points line honours the competition's customised points when present.
const matchedPoints = computed(() => {
  if (!matchedOutcome.value) return null;
  const override = (props.overrides ?? []).find(
      (o) => o.scoringSystemOutcomeId === matchedOutcome.value.id);
  return {
    home: override?.homePoints ?? matchedOutcome.value.homePoints,
    away: override?.awayPoints ?? matchedOutcome.value.awayPoints,
    customised: !!override,
  };
});
</script>

<template>
  <span>
    <v-btn
        v-if="scoringSystemId || load"
        variant="text"
        size="small"
        prepend-icon="mdi-eye-outline"
        @click="open"
    >{{ label }}</v-btn>

    <tl-dialog v-model="dialog" :title="`Preview: ${previewSystem?.name ?? 'Scoring system'}`" :max-width="560">
      <v-skeleton-loader v-if="itemPending" type="article" />
      <div v-else-if="loadError" class="text-body-2 text-error">{{ loadError }}</div>
      <template v-else-if="previewSystem">
        <div v-if="previewSystem.description" class="text-body-2 mb-2">
          {{ previewSystem.description }}
        </div>
        <div class="text-body-2 text-medium-emphasis mb-3">
          Try the exact score input players will see. Nothing here is saved.
        </div>

        <!-- same segmented control as everywhere else (tl-seg, ink fill) -->
        <div v-if="supportsLive" class="tl-seg mb-4">
          <button type="button" class="tl-seg__opt" :class="{'tl-seg__opt--active': tab === 'entry'}"
                  @click="tab = 'entry'">Result entry</button>
          <button type="button" class="tl-seg__opt" :class="{'tl-seg__opt--active': tab === 'live'}"
                  @click="tab = 'live'">Live counter</button>
        </div>

        <v-window v-model="tab">
          <v-window-item value="entry">
            <scoring-system-input
                v-model:scoring-system="previewSystem"
                v-model:home-result="homeResult"
                v-model:away-result="awayResult"
                v-model:games="games"
                :home-team="homeTeam"
                :away-team="awayTeam"
                :hide-system-banner="true"
            />
            <v-alert
                v-if="matchedOutcome"
                type="success"
                variant="tonal"
                density="compact"
                class="mt-3"
            >
              Outcome: {{ matchedOutcome.homeResult }}–{{ matchedOutcome.awayResult }},
              {{ matchedPoints.home }} pts home, {{ matchedPoints.away }} pts away<template v-if="matchedPoints.customised"> (customised)</template>
            </v-alert>
            <div class="d-flex justify-end mt-2">
              <v-btn size="small" variant="text" prepend-icon="mdi-restart" @click="resetEntry">
                Reset
              </v-btn>
            </div>
          </v-window-item>

          <v-window-item v-if="supportsLive" value="live">
            <live-counter :scoring="preview" :preview="true" home-name="Home" away-name="Away" />
            <div class="d-flex justify-end mt-2">
              <v-btn size="small" variant="text" prepend-icon="mdi-restart" @click="preview.reset()">
                Restart match
              </v-btn>
            </div>
          </v-window-item>
        </v-window>
      </template>
    </tl-dialog>
  </span>
</template>
